import { AiTextButton } from '~/features/ai-studio/components'
import { applyGeneratedText } from '~/features/ai-studio/lib/apply-generated-text'
import { AmenitySelector } from '~/features/shared/components/amenity-selector'
import { TagSelector } from '~/features/shared/components/tag-selector'
import * as m from '~/paraglide/messages'
import { createFieldValidators } from '~/shared/lib/form-utils'
import { createListingSchema } from '~/shared/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { FormInput, FormTextarea } from '~/shared/ui/form'
import { PriceLevelControl } from './price-level-control'

interface ListingDetailsSectionProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API
  form: any
}

export function ListingDetailsSection({ form }: ListingDetailsSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.listingDetails()}</CardTitle>
        <CardDescription>{m.basicInformationAboutYourBusiness()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form.Field name="name" validators={createFieldValidators(createListingSchema.shape.name)}>
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <FormInput field={field} label={m.businessName()} placeholder={m.myRestaurant()} />
          )}
        </form.Field>

        <div className="space-y-2">
          <form.Field name="nicknames">
            {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
            {(field: any) => (
              <FormInput
                field={field}
                label={m.listingNicknames()}
                placeholder={m.listingNicknamesPlaceholder()}
                optional
                maxLength={200}
              />
            )}
          </form.Field>
          <p className="text-muted-foreground text-sm">{m.listingNicknamesHint()}</p>
        </div>

        <form.Field
          name="description"
          validators={createFieldValidators(createListingSchema.shape.description)}
        >
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <FormTextarea
              field={field}
              label={m.description()}
              placeholder={m.describeYourBusinessInDetail()}
              labelAction={
                <AiTextButton
                  kind="place-description"
                  onUse={(text) => applyGeneratedText(field, text)}
                />
              }
            />
          )}
        </form.Field>

        <form.Field name="tags">
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <TagSelector
              selectedCategoryId={field.state.value?.mainCategoryId}
              selectedSubCategoryIds={field.state.value?.subCategoryTags || []}
              onCategoryChange={(categoryId) => {
                field.handleChange({
                  ...field.state.value,
                  mainCategoryId: categoryId,
                  subCategoryTags: [],
                })
              }}
              onSubCategoriesChange={(subCategoryIds) => {
                field.handleChange({
                  ...field.state.value,
                  subCategoryTags: subCategoryIds,
                })
              }}
              maxSubCategories={3}
            />
          )}
        </form.Field>

        <form.Field name="priceLevel">
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <div className="space-y-2">
              <p aria-hidden className="font-medium text-sm leading-none">
                {m.listingPriceLevel()}
              </p>
              <div>
                <PriceLevelControl
                  value={field.state.value ?? ''}
                  onChange={(value) => field.handleChange(value)}
                />
              </div>
              <p className="text-muted-foreground text-sm">{m.listingPriceLevelHint()}</p>
            </div>
          )}
        </form.Field>

        <form.Field name="tags">
          {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
          {(field: any) => (
            <AmenitySelector
              selectedAmenityIds={field.state.value?.amenityIds || []}
              onAmenitiesChange={(amenityIds) => {
                field.handleChange({
                  ...field.state.value,
                  amenityIds,
                })
              }}
            />
          )}
        </form.Field>
      </CardContent>
    </Card>
  )
}
